import {BaseRouteMetaDataModel} from "../../../infrastructure/models/route/base-route.meta-data.model";
import {computed} from "mobx";
import {EntityDescriptionSectionViewModel} from "../../../domain/entity-description/models/section/entity-description.section.view-model";
import {WebsiteModuleState} from "../../../apps/website/models/website-module.state";
import {MetadataManager} from "../../../core/managers/metadata.manager";
import {SchemaManager} from "../../../core/managers/schema-manager";
import {BaseSchemaModel} from "../../../core/models/schema/base-schema-model";
import {SITE_DOMAINS} from "../../../core/consts";
import {HrefModel} from "../../../core/models/meta-data/href-model";
import {DEFAULT_CULTURE_NAME, SPORT_TYPES} from "../../../core/app/consts";
import {WEBSITE_MODULE} from "../../../apps/website/website-module.consts";
import {ARGS} from "../../../core/bootstrap/args";

export class CompetitionPageMetaDataModel extends BaseRouteMetaDataModel {
  constructor(ioc, state) {
    super(ioc);

    this.state = state
  }

  @computed get websiteModuleState() {
    const websiteModuleState: WebsiteModuleState = this.resolve(WebsiteModuleState);

    return websiteModuleState
  }

  @computed get websiteTerms() {
    return this.websiteModuleState.terms
  }

  @computed get metadataManager() {
    return this.resolve(MetadataManager);
  }

  @computed get hasSections() {
    return this.metadataManager.hasSections
  }

  @computed get schemaManager(): SchemaManager {
    return this.resolve(SchemaManager);
  }

  @computed get titleSection(): EntityDescriptionSectionViewModel {
    if(this.hasSections) {
      const titleSection = this.metadataManager.sections.first()
      const section: EntityDescriptionSectionViewModel = new EntityDescriptionSectionViewModel(titleSection);

      return section;
    }

    return null
  }

  @computed get descriptionSection() {
    if(this.hasSections) {
      const descriptionSection = this.metadataManager.sections.last()
      const section: EntityDescriptionSectionViewModel = new EntityDescriptionSectionViewModel(descriptionSection);

      return section;
    }

    return null
  }

  descriptionFormatTerm(filterTerm) {
    const description: String = filterTerm.format({"COMPETITION_NAME": this.competitionName});

    return description
  }

  get competitionId() {
    return this.websiteModuleState.entitiesContext.competition?.id
  }

  get competitionName() {
    return this.websiteModuleState.entitiesContext.competition?.longName || this.websiteModuleState.entitiesContext.competition?.name
  }
  get competitionSportName() {
    return this.websiteModuleState.entitiesContext.competition?.sport?.name || ''
  }

  get competitionCurrentYear() {
    return this.websiteModuleState.entitiesContext.competition?.currentSeason?.name
  }

  get hasStandingsGroups() {
    return this.websiteModuleState.entitiesContext.competition?.hasStandingsGroups
  }

  get bracketsName(): String {
    return this.websiteModuleState.entitiesContext.competition?.bracketsName
  }

  get dataSchema(): Array<BaseSchemaModel> {
    if(!this.websiteModuleState?.entitiesContext.competition) {
      return null;
    }

    return [this.websiteModuleState?.entitiesContext.competition && this.schemaManager.createSportOrganization(this.websiteModuleState?.entitiesContext.competition)];
  }

  get canonicalUrl(): String {
    let path: String = window.location.pathname;
    let href: String = (`${SITE_DOMAINS.full}${path}`).replace(/\?.*$/, '');

    const lastChar = href.charAt(href.length - 1);
    if (lastChar === '/') {
      return href.substring(0, href.length - 1)
    }

    return href;
  }

  get languageRepository() {
    return this.websiteModuleState.languageRepository;
  }

  get pathWithoutCulture(): String {
    let path: String = window.location.pathname;
    const cultureName: String = this.languageRepository.selectedLanguage?.displayName;

    if (cultureName && cultureName !== DEFAULT_CULTURE_NAME) {
      const culturePrefix: String = '/' + cultureName;
      if (path.startsWith(culturePrefix + '/') || path === culturePrefix) {
        path = path.substring(culturePrefix.length) || '/';
      }
    }

    return path;
  }

  get xDefaultLink(): String {
    let href: String = (`${SITE_DOMAINS.full}${this.pathWithoutCulture}`).replace(/\?.*$/, '');

    const lastChar = href.charAt(href.length - 1);
    if (lastChar === '/') {
      return href.substring(0, href.length - 1)
    }

    return href;
  }

  get alternateLinks(): Array<HrefModel> {
    const languages = this.languageRepository.languages?.filter((lang) => lang.displayName !== DEFAULT_CULTURE_NAME);

    return languages?.map((lang) => {
      let href: String = (`${SITE_DOMAINS.full}/${lang.displayName}${this.pathWithoutCulture}`).replace(/\?.*$/, '');
      const lastChar = href.charAt(href.length - 1);
      if (lastChar === '/') {
        href = href.substring(0, href.length - 1);
      }

      return new HrefModel(href, this.hrefLangName(lang));
    });
  }

  hrefLangName(lang): String {
    const cultureNameLanguages: Array<Number> = Object.values(WEBSITE_MODULE.HREF_CULTURE_NAME_LANG_ID);

    return cultureNameLanguages.includes(lang.id) ? lang.cultureName : lang.displayName;
  }

  get isStoreDomain(): Boolean {
    return this.websiteModuleState.isStoreDomain;
  }

  @computed get isNoIndex(): Boolean {
    const isHindiLanguage: Boolean = this.languageRepository.selectedLanguage.id === 216;
    const isCricket: Boolean = this.websiteModuleState.entitiesContext.competition?.sport?.id === SPORT_TYPES.cricket;

    return ARGS.NO_INDEX === 'true' || this.websiteModuleState.entitiesContext.competition.hide || (isHindiLanguage && !isCricket);
  }
}